import { Button, Divider } from "@heroui/react";
import { clsx } from "clsx";
import { useTranslations } from "translations";
import type { Step } from "./types";

type StepStatus = "active" | "complete" | "inactive";

type StepItemProps<T extends number> = {
  currentStep: T;
  isInteractive: boolean;
  isLast: boolean;
  onStepChange: (stepIndex: T) => void;
  step: Step;
  stepClassName?: string;
  stepIndex: T;
};

const STEP_CIRCLE_CLASSES: Record<StepStatus, string> = {
  active: "bg-transparent border-primary text-primary",
  complete: "bg-primary border-primary text-white",
  inactive: "bg-gray-200 border-gray-200 text-black",
};

const LABEL_CLASSES: Record<StepStatus, string> = {
  active: "text-black",
  complete: "text-black",
  inactive: "text-gray-600",
};

const LINE_CLASSES = {
  complete: "bg-primary",
  default: "bg-gray-300",
};

/**
 * Determines the status of a step based on the current active position.
 */
function getStepStatus<T extends number>(
  currentStep: T,
  stepIndex: T,
): StepStatus {
  if (currentStep === stepIndex) return "active";
  if (currentStep < stepIndex) return "inactive";
  return "complete";
}

export function StepItem<T extends number>({
  currentStep,
  isInteractive,
  isLast,
  onStepChange,
  step,
  stepClassName,
  stepIndex,
}: StepItemProps<T>) {
  const translations = useTranslations();

  const status = getStepStatus(currentStep, stepIndex);

  const ariaLabel =
    step["aria-label"] ??
    translations.accessibility.stepperCurrentStepAriaLabel({
      currentStep: (stepIndex + 1).toString(),
    });

  const ariaCurrent = status === "active" ? "step" : undefined;

  const innerClassName = clsx(
    "group flex w-full flex-row items-center justify-center gap-x-3 rounded-large py-2.5",
    stepClassName,
  );

  const onPress = () => onStepChange(stepIndex);

  const stepRow = (
    <>
      <div className="relative flex items-center">
        <div
          className={clsx(
            "relative flex h-7.5 w-7.5 items-center justify-center rounded-full border-medium text-body-md font-semibold text-black",
            STEP_CIRCLE_CLASSES[status],
            status === "active" ? "font-bold" : "font-medium",
          )}
        >
          <span>{stepIndex + 1}</span>
        </div>
      </div>
      {step.label && (
        <div className="max-w-full flex-1 text-start">
          <div
            className={clsx(
              "text-small transition-[color,opacity] duration-300 group-active:opacity-80 lg:text-medium",
              LABEL_CLASSES[status],
              status === "active" ? "font-bold" : "font-medium",
            )}
          >
            {step.label}
          </div>
        </div>
      )}
      {!isLast && (
        <div
          aria-hidden="true"
          className="pointer-events-none flex w-10 flex-none items-center"
        >
          <Divider
            aria-hidden="true"
            className={clsx(
              "relative h-0.5 w-full transition-colors duration-300",
              currentStep > stepIndex
                ? LINE_CLASSES.complete
                : LINE_CLASSES.default,
            )}
            role="presentation"
          />
        </div>
      )}
    </>
  );

  return (
    <li aria-label={ariaLabel} className="relative flex w-full items-center">
      {isInteractive ? (
        <Button
          aria-current={ariaCurrent}
          className={clsx(
            innerClassName,
            // Strip default HeroUI Button sizing/styling so the stepper layout drives appearance
            "h-auto! min-h-0! min-w-0! bg-transparent! px-0!",
            "data-[hover=true]:bg-transparent! data-[hover=true]:opacity-100!",
            "data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-offset-4! data-[focus-visible=true]:outline-focus",
          )}
          disableAnimation
          disableRipple
          variant="light"
          onPress={onPress}
        >
          {stepRow}
        </Button>
      ) : (
        <div aria-current={ariaCurrent} className={innerClassName}>
          {stepRow}
        </div>
      )}
    </li>
  );
}

StepItem.displayName = "RecareUI.StepItem";
